import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/hooks/useRedux';
import { GroupsFilter } from 'src/components/FIlterPolygons/GroupsFilter';
import { Button } from 'src/components/ui/Button';
import { setPolygonsFilter } from 'src/store/filters';
import { StatusesFilter } from 'src/components/FIlterPolygons/StatusesFilter';
import { TypeOwnersFilter } from 'src/components/FIlterPolygons/TypeOwnersFilter';
import { BlockStatusesFilter } from 'src/components/FIlterPolygons/BlockStatusesFilter';
import { OwnersFilter } from 'src/components/FIlterPolygons/OwnersFilter';
import { Loader } from 'src/components/ui/Loader';
import { fetchInfoData } from 'src/store/polygons/actions';
import { PolygonsFilterType } from 'src/config/types';

import cn from 'classnames';
import { ScrollButton } from 'src/components/ScrollButton';
import { useWindowDimensions } from 'src/hooks/useWindowDimension';
import { PurposesFilter } from './PurposesFilter';
import { ManagementFunctionsFilter } from './ManagementFunctionsFilter';
import { ContoursFunctionsFilter } from './ContoursFunctionsFilter';

interface FilterPolygonsProps {
  handleClose: () => void;
  classNames?: string;
}

export const FilterPolygons: FC<FilterPolygonsProps> = ({ handleClose, classNames }) => {
  const { polygonsFilter } = useAppSelector(state => state.filters);
  const { isLoadingAllInfo } = useAppSelector(state => state.categories);

  const { width } = useWindowDimensions();
  const [tempFilter, setTempFilter] = useState<PolygonsFilterType>(polygonsFilter);
  const [showScrollButton, setShowScrollButton] = useState<'top' | 'bottom'>();

  const dispatch = useAppDispatch();
  const ref = useRef<HTMLDivElement>(null);

  const isFilterActive = useMemo(() => !!Object.keys(polygonsFilter)?.length, [polygonsFilter]);

  const handleSelect = <K extends keyof PolygonsFilterType>(
    value: NonNullable<PolygonsFilterType[K]>[number],
    filterName: K,
  ) => {
    const currentValues = (tempFilter?.[filterName] ?? []) as NonNullable<PolygonsFilterType[K]>;

    const exists = currentValues.includes(value as any);
    const newValues = exists
      ? currentValues.filter(v => v !== (value as any))
      : [...currentValues, value as any];

    setTempFilter(prev => ({ ...prev, [filterName]: newValues }));
  };

  const handleSaveFilters = () => {
    if (JSON.stringify(tempFilter.categories) !== JSON.stringify(polygonsFilter.categories)) {
      dispatch(fetchInfoData(tempFilter?.categories || []));
    }

    dispatch(setPolygonsFilter(tempFilter));
    handleClose();
  };

  const handleResetFilters = () => {
    dispatch(fetchInfoData([]));
    setTempFilter({});
    dispatch(setPolygonsFilter({}));
    handleClose();
  };

  const getTitle = (title: string) => (
    <div className="text-extra-xs dark:text-white font-gothampro-400 opacity-50 mb-3">{title}</div>
  );

  const smoothScrollTo = (id: string) => {
    if (!ref?.current) return;

    const block = document.querySelector(`#${id}`) as HTMLDivElement;

    if (!block) return;

    ref.current.scrollTo({
      top: block.offsetHeight + block.offsetTop - ref.current.clientHeight,
      left: 0,
      behavior: 'smooth',
    });
  };

  const props = { handleSelect, tempFilter, smoothScrollTo };

  const filters: { label: string; component: ReactNode; id?: string }[] = [
    {
      label: 'Контур',
      component: <ContoursFunctionsFilter {...props} />,
      id: 'contours',
    },
    {
      label: 'Функции управления',
      component: <ManagementFunctionsFilter {...props} />,
      id: 'management_functions',
    },
    {
      label: 'Направления',
      component: <BlockStatusesFilter {...props} />,
      id: 'blockStatus',
    },
    {
      label: 'Назначение',
      component: <PurposesFilter {...props} />,
      id: 'purposes',
    },
    {
      label: 'Группы объектов',
      component: <GroupsFilter {...props} />,
      id: 'group',
    },
    {
      label: 'Правообладатели',
      component: <OwnersFilter {...props} />,
      id: 'owner',
    },
    {
      label: 'Статус',
      component: <StatusesFilter {...props} />,
      id: 'status',
    },
    {
      label: 'Вид права',
      component: <TypeOwnersFilter {...props} />,
      id: 'typeOwner',
    },
  ];

  useEffect(() => {
    const container = ref?.current;

    if (!container) {
      return;
    }

    const { clientHeight, scrollHeight, scrollTop } = container;

    const scroll = scrollHeight - clientHeight;

    setShowScrollButton(scroll / 2 < scrollTop ? 'top' : 'bottom');

    const handleScroll = () => {
      const { scrollTop } = container;
      setShowScrollButton(scroll / 2 < scrollTop ? 'top' : 'bottom');
    };

    container.addEventListener('scroll', handleScroll);

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className={cn('relative', classNames)}>
      {isLoadingAllInfo ? (
        <div className="t-lg:w-[34rem] d-xs:w-[40rem] h-[40vh] d-xs:h-[50vh] flex items-center justify-center py-10">
          <Loader classNames="w-20 h-20" />
        </div>
      ) : null}

      {!isLoadingAllInfo ? (
        <>
          <div
            className="max-h-[40vh] d-xs:max-h-[50vh] overflow-auto flex flex-col gap-7 pr-2"
            ref={ref}
          >
            {filters.map(filter => (
              <div
                key={`filter-list-${filter.label}`}
                id={`filter-${filter.id}`}
                className="w-full relative"
              >
                {getTitle(filter.label)}
                {filter.component}
              </div>
            ))}
          </div>

          <ScrollButton
            visible={!!showScrollButton}
            variant={showScrollButton}
            classNames="bottom-20"
            onClick={() =>
              smoothScrollTo(`filter-${showScrollButton === 'top' ? 'blockStatus' : 'typeOwner'}`)
            }
          />

          <div className="flex flex-col t-sm:flex-row gap-2 t-sm:gap-5 justify-center pt-0 d-xs:pt-5">
            {isFilterActive ? (
              <Button
                variant="grey"
                size={width > 1200 ? 'big' : 'small'}
                classNames="w-full t-sm:w-60"
                onClick={handleResetFilters}
              >
                Сбросить все фильтры
              </Button>
            ) : null}
            <Button
              variant="blue"
              size={width > 1200 ? 'big' : 'small'}
              classNames="w-full t-sm:w-60"
              onClick={handleSaveFilters}
            >
              Применить
            </Button>
          </div>
        </>
      ) : null}
    </div>
  );
};
